import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import cmsService, { extractGigcasterIdsFromChannel } from '../services/CMSService';
import './FightCard.css';

const PLACEHOLDER_IMG = '/assets/images/placeholder_fighter.png';

const FighterColumn = ({ fighter, onClick, align }) => {
  const [imgError, setImgError] = useState(false);
  const src = !fighter.image || imgError ? PLACEHOLDER_IMG : fighter.image;

  return (
    <div className={`fight-card__fighter fight-card__fighter--${align}`}>
      <button className="fight-card__portrait" onClick={onClick} type="button">
        <img
          src={src}
          alt={fighter.name}
          className="fight-card__portrait-img"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </button>
      <span className="fight-card__name">{fighter.name}</span>
    </div>
  );
};

const StatRow = ({ left, label, right, noBorder }) => (
  <div className={`fight-card__stat-row${noBorder ? ' fight-card__stat-row--no-border' : ''}`}>
    <span className="fight-card__stat-value fight-card__stat-value--right">{left || '—'}</span>
    <span className="fight-card__stat-label">{label}</span>
    <span className="fight-card__stat-value fight-card__stat-value--left">{right || '—'}</span>
  </div>
);

const SingleFightCard = ({ card, onFighterClick }) => {
  const { fighters, event } = card;
  const f1 = fighters.fighter1;
  const f2 = fighters.fighter2;

  const parseCountryImg = raw => {
    try {
      return JSON.parse(raw || '{}')?.url || null;
    } catch {
      return null;
    }
  };

  const f1Flag = parseCountryImg(f1.fullFighter?.country_img);
  const f2Flag = parseCountryImg(f2.fullFighter?.country_img);

  return (
    <div className="fight-card">
      <div className="fight-card__championship">{event?.championship || '\u00A0'}</div>

      <div className="fight-card__matchup">
        <FighterColumn fighter={f1} onClick={() => onFighterClick(f1)} align="left" />
        <div className="fight-card__vs">VS</div>
        <FighterColumn fighter={f2} onClick={() => onFighterClick(f2)} align="right" />
      </div>

      <StatRow
        left={
          <span className="fight-card__country">
            {f1Flag && <img src={f1Flag} alt="" className="fight-card__flag" />}
            {f1.fullFighter?.country_code || ''}
          </span>
        }
        label="COUNTRY"
        right={
          <span className="fight-card__country">
            {f2.fullFighter?.country_code || ''}
            {f2Flag && <img src={f2Flag} alt="" className="fight-card__flag" />}
          </span>
        }
      />
      <StatRow left={f1.record} label="W - D - L" right={f2.record} />
      <StatRow left={f1.weight} label="WEIGHT" right={f2.weight} noBorder />
    </div>
  );
};

const FightCardsSection = ({ channel }) => {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('maincard');
  const [gigcasterIds, setGigcasterIds] = useState([]);
  const [isResolvingGigcasterIds, setIsResolvingGigcasterIds] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function resolveGigcasterIds() {
      setIsResolvingGigcasterIds(true);
      setLoading(true);

      const directIds = extractGigcasterIdsFromChannel(channel);
      const slug = channel?.slug || channel?.strSlug;

      if (directIds.length > 0) {
        if (!cancelled) {
          setGigcasterIds(directIds);
          setIsResolvingGigcasterIds(false);
        }
        return;
      }

      if (!slug) {
        if (!cancelled) {
          setGigcasterIds([]);
          setIsResolvingGigcasterIds(false);
          setLoading(false);
        }
        return;
      }

      try {
        const resolvedIds = await cmsService.resolveGigcasterIdsForChannel(channel);
        if (!cancelled) {
          setGigcasterIds(resolvedIds);
          if (resolvedIds.length === 0) {
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setGigcasterIds([]);
          setLoading(false);
        }
      } finally {
        if (!cancelled) {
          setIsResolvingGigcasterIds(false);
        }
      }
    }

    resolveGigcasterIds();
    return () => {
      cancelled = true;
    };
  }, [channel]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCards() {
      if (isResolvingGigcasterIds) {
        return;
      }

      if (gigcasterIds.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      for (const gid of gigcasterIds) {
        try {
          const result = await cmsService.getCardDataByGigcasterId(gid);
          if (!cancelled && result && result.length > 0) {
            setCards(result);
            setLoading(false);
            return;
          }
        } catch {}
      }

      if (!cancelled) {
        setCards(null);
        setLoading(false);
      }
    }

    fetchCards();
    return () => {
      cancelled = true;
    };
  }, [gigcasterIds, isResolvingGigcasterIds]);

  const mainCardFights = useMemo(
    () => (cards || []).filter(c => c.fighters?.is_main_card),
    [cards],
  );
  const prelimFights = useMemo(() => (cards || []).filter(c => !c.fighters?.is_main_card), [cards]);

  const handleFighterClick = fighter => {
    if (fighter.fullFighter?.id) {
      navigate(`/fighters/${fighter.fullFighter.id}`);
    }
  };

  if (!isResolvingGigcasterIds && gigcasterIds.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="fight-cards-section">
        <div className="fight-cards-section__loading">
          <div className="fight-cards-section__spinner" />
          <span>Loading fight card...</span>
        </div>
      </div>
    );
  }

  if (!cards || cards.length === 0) {
    return null;
  }

  const eventInfo = cards[0]?.event;
  const eventLocation = [eventInfo?.venue_name, eventInfo?.location].filter(Boolean).join(' · ');
  const eventDateAndLocation = [eventInfo?.date, eventLocation].filter(Boolean).join(' · ');

  return (
    <div className="fight-cards-section">
      <div className="fight-cards-section__header">
        <h3 className="fight-cards-section__title">Fight Card</h3>
        {eventInfo && (
          <div className="fight-cards-section__event-info">
            <span className="fight-cards-section__event-name">{eventInfo.title}</span>
            {eventDateAndLocation && (
              <span className="fight-cards-section__event-date">{eventDateAndLocation}</span>
            )}
          </div>
        )}
      </div>

      <div className="fight-cards-section__tabs">
        <button
          className={`fight-cards-section__tab${activeTab === 'maincard' ? ' fight-cards-section__tab--active' : ''}`}
          onClick={() => setActiveTab('maincard')}
          type="button"
        >
          Main Card ({mainCardFights.length})
        </button>
        <button
          className={`fight-cards-section__tab${activeTab === 'prelims' ? ' fight-cards-section__tab--active' : ''}`}
          onClick={() => setActiveTab('prelims')}
          type="button"
        >
          Free Fights ({prelimFights.length})
        </button>
      </div>

      <div className="fight-cards-section__content">
        {activeTab === 'maincard' &&
          (mainCardFights.length > 0 ? (
            <div className="fight-cards-section__grid">
              {mainCardFights.map((card, idx) => (
                <SingleFightCard key={idx} card={card} onFighterClick={handleFighterClick} />
              ))}
            </div>
          ) : (
            <p className="fight-cards-section__empty">No main card fights available.</p>
          ))}

        {activeTab === 'prelims' &&
          (prelimFights.length > 0 ? (
            <div className="fight-cards-section__grid">
              {prelimFights.map((card, idx) => (
                <SingleFightCard key={idx} card={card} onFighterClick={handleFighterClick} />
              ))}
            </div>
          ) : (
            <p className="fight-cards-section__empty">No free fights available.</p>
          ))}
      </div>
    </div>
  );
};

export default FightCardsSection;
